/**
 * Contrast utilities for adaptive text color on canvas.
 *
 * These functions sample the background already rendered on the canvas and
 * pick a text color (near-black or near-white) that maximizes readability.
 * No tinted rectangles, halos, shadows or strokes are used.
 */

/**
 * Return a high-contrast text color for the given background.
 *
 * @param {string | number[] | null} bgHexOrRgb - Hex color (#rrggbb) or [r, g, b] array.
 * @returns {string} '#1a1a1a' for light backgrounds, '#f5f5f5' for dark backgrounds.
 */
export function getContrastColor(bgHexOrRgb) {
  let r, g, b

  if (typeof bgHexOrRgb === 'string' && bgHexOrRgb.startsWith('#')) {
    const hex = bgHexOrRgb.replace('#', '')
    r = parseInt(hex.substr(0, 2), 16)
    g = parseInt(hex.substr(2, 2), 16)
    b = parseInt(hex.substr(4, 2), 16)
  } else if (Array.isArray(bgHexOrRgb) && bgHexOrRgb.length >= 3) {
    [r, g, b] = bgHexOrRgb
  } else {
    return '#1a1a1a'
  }

  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000
  return yiq >= 128 ? '#1a1a1a' : '#f5f5f5'
}

function hexToRgb(hex) {
  const h = (hex || '').replace('#', '')
  if (h.length < 6) return [0, 0, 0]
  return [
    parseInt(h.substr(0, 2), 16),
    parseInt(h.substr(2, 2), 16),
    parseInt(h.substr(4, 2), 16),
  ]
}

function compositeOverBackground(rgba, bgHex) {
  const [br, bg, bb] = hexToRgb(bgHex)
  const alpha = rgba[3] / 255
  if (alpha === 0) return [br, bg, bb]
  return [
    Math.round(rgba[0] * alpha + br * (1 - alpha)),
    Math.round(rgba[1] * alpha + bg * (1 - alpha)),
    Math.round(rgba[2] * alpha + bb * (1 - alpha)),
  ]
}

/**
 * Sample the average color of a small square region of the canvas.
 *
 * Coordinates are expected in CSS pixels; the function accounts for the
 * device pixel ratio internally.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} x - CSS x coordinate.
 * @param {number} y - CSS y coordinate.
 * @param {number} [sampleSize=5] - Size of the square sample (odd recommended).
 * @returns {number[] | null} [r, g, b, a] or null if sampling fails.
 */
export function sampleBackgroundColor(ctx, x, y, sampleSize = 5) {
  const canvas = ctx.canvas
  if (!canvas) return null

  const dpr = window.devicePixelRatio || 1
  const half = Math.floor(sampleSize / 2)

  const sxRaw = Math.round(x * dpr) - half
  const syRaw = Math.round(y * dpr) - half

  const sx = Math.max(0, Math.min(canvas.width - sampleSize, sxRaw))
  const sy = Math.max(0, Math.min(canvas.height - sampleSize, syRaw))

  try {
    const imageData = ctx.getImageData(sx, sy, sampleSize, sampleSize)
    let r = 0
    let g = 0
    let b = 0
    let a = 0
    const count = imageData.data.length / 4

    if (count === 0) return null

    for (let i = 0; i < imageData.data.length; i += 4) {
      r += imageData.data[i]
      g += imageData.data[i + 1]
      b += imageData.data[i + 2]
      a += imageData.data[i + 3]
    }

    return [
      Math.round(r / count),
      Math.round(g / count),
      Math.round(b / count),
      Math.round(a / count),
    ]
  } catch {
    return null
  }
}

/**
 * Draw text on the canvas using a color automatically chosen from the
 * background at the text position.
 *
 * The canvas overlay is transparent over the chart, so the sampled color is
 * composited over the provided chart background to estimate the visible color.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} text
 * @param {number} x - CSS x coordinate.
 * @param {number} y - CSS y coordinate (baseline).
 * @param {string} fallbackColor - Color used when sampling fails.
 * @param {string} [chartBackground='#111827'] - Chart background color for alpha compositing.
 * @param {CanvasTextAlign} [align='left']
 * @param {CanvasTextBaseline} [baseline='alphabetic']
 */
export function drawAdaptiveText(
  ctx,
  text,
  x,
  y,
  fallbackColor,
  chartBackground = '#111827',
  align = 'left',
  baseline = 'alphabetic'
) {
  ctx.save()
  ctx.textAlign = align
  ctx.textBaseline = baseline
  const sample = sampleBackgroundColor(ctx, x, y, 5)
  const visibleBg = sample ? compositeOverBackground(sample, chartBackground) : hexToRgb(chartBackground)
  ctx.fillStyle = getContrastColor(visibleBg)
  ctx.fillText(text, x, y)
  ctx.restore()
}
